import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { pathManager } from 'routes';

import { Box, Card, CardContent, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import Button from '@libs/ui/components/button';
import Typography, { RouterLink } from '@libs/ui/components/typography';

import { TypeCard } from './TypeCard';
import { JoinTypeEnum } from './types';

interface AddCompanyFormProps {}

export const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  card: {
    marginLeft: 'auto',
    background: '#1E1E1A',
    textAlign: 'center',
    borderRadius: theme.spacing(4),

    '& a': {
      color: `${theme.palette.info.main} !important`,
    },
  },
  createText: {
    cursor: 'pointer',
    color: theme.palette.info.main,
  },
  cardContent: {
    padding: `${theme.spacing(14)} !important`,

    [theme.breakpoints.down('md')]: {
      padding: `${theme.spacing(6)} !important`,
    },
  },
  yellowText: {
    color: 'rgba(255, 255, 255, 0.54) !important',
  },
  btn: {
    borderRadius: 34,

    'a&': {
      color: `${theme.palette.text.primary} !important`,
    },

    '&.Mui-disabled': {
      background: '#BDBDBD',
    },
  },
}));

export const AddCompanyForm = (props: AddCompanyFormProps) => {
  const [currentType, setCurrentType] = useState<JoinTypeEnum>();
  const classes = useStyles();
  const { t } = useTranslation('companyOnboarding');
  const history = useHistory();

  const cardsData = useMemo(
    () => [
      {
        title: t('v4.chooseType.types.company'),
        text: t('v4.chooseType.types.companyDesc'),
        value: JoinTypeEnum.Company,
      },
      {
        title: t('v4.chooseType.types.recruiter'),
        text: t('v4.chooseType.types.recruiterDesc'),
        value: JoinTypeEnum.Independent,
      },
      {
        title: t('v4.chooseType.types.agency'),
        text: t('v4.chooseType.types.agencyDesc'),
        value: JoinTypeEnum.Agency,
      },
    ],
    [t],
  );
  const onSubmit = () => {
    if (!currentType) {
      return;
    }
    if (currentType === JoinTypeEnum.Company) {
      history.push(pathManager.company.onboardingV4.create.generatePath());
      return;
    }

    if (currentType === JoinTypeEnum.Agency) {
      history.push(pathManager.company.onboardingV4.bookAgency.generatePath());
      return;
    }

    window.location.href = 'https://www.opentalent.co/program';
  };

  return (
    <div className={classes.root}>
      <Card elevation={0} className={classes.card}>
        <CardContent classes={{ root: classes.cardContent }}>
          <Box mb={3}>
            <Typography style={{ fontSize: 24 }} variant="h5" fontWeight={600}>
              {t('v4.chooseType.title')}
            </Typography>
          </Box>

          <Box mb={6}>
            <Typography variant="body1">
              {t('v4.chooseType.subtitle')}
            </Typography>
          </Box>

          <Grid container flexDirection="column" spacing={4} mb={8}>
            {cardsData.map((card) => (
              <Grid item key={card.value}>
                <TypeCard
                  {...card}
                  onChange={() => setCurrentType(card.value)}
                  selected={currentType === card.value}
                />
              </Grid>
            ))}
          </Grid>

          <Box mb={6}>
            <Button
              fullWidth
              size="large"
              color="primary"
              variant="contained"
              className={classes.btn}
              disabled={!currentType}
              onClick={onSubmit}
            >
              {t('v4.chooseType.continue')}
            </Button>
          </Box>

          <Box>
            <Typography variant="subtitle2" style={{ fontSize: 14 }}>
              {t('v4.chooseType.alreadyHaveAccount')}{' '}
              <RouterLink
                to={pathManager.quickAuth.generatePath()}
                color="info.main"
                style={{ fontSize: 14 }}
              >
                {t('v4.chooseType.signIn')}
              </RouterLink>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};
